import type { Immutable } from 'immer';
import { z } from 'zod';

import { ILogger } from '@tdp/common/telemetry';

import { BotEventSubscription, BotEventSubscriptionSchema } from './models.eventSubscription';

export interface BotEventSubscriptionSuccessResult {
  type: 'success';
  subscription: BotEventSubscription;
}
export interface BotEventSubscriptionNetworkErrorResult {
  type: 'networkError';
  errorCode: number;
}
export interface BotEventSubscriptionParseErrorResult {
  type: 'parseError';
  errors: z.ZodError;
}
export type BotEventSubscriptionResult =
  | BotEventSubscriptionSuccessResult
  | BotEventSubscriptionNetworkErrorResult
  | BotEventSubscriptionParseErrorResult;

export interface IBotEventSubscriptionProvider {
  getBotEventSubscription(botId: string): Promise<BotEventSubscriptionResult>;
}
export interface IBotEventSubscriptionWriter {
  putBotEventSubscription(botId: string, subscription: Immutable<BotEventSubscription>): Promise<BotEventSubscriptionResult>;
}

export interface BotEventSubscriptionServiceDeps {
  logger: ILogger;
  apiFetch: typeof fetch;
}

export class TdpBotEventSubscriptionService implements IBotEventSubscriptionProvider, IBotEventSubscriptionWriter {
  private logger: ILogger;
  private apiFetch: typeof fetch;
  constructor(deps: BotEventSubscriptionServiceDeps) {
    this.apiFetch = deps.apiFetch;
    this.logger = deps.logger;
  }
  async getBotEventSubscription(botId: string): Promise<BotEventSubscriptionResult> {
    const response = await this.apiFetch(`/botframework/${botId}/meetings/subscription`);
    if (response.status < 200 || response.status >= 300) {
      return { type: 'networkError', errorCode: response.status };
    }
    const body = (await response.json()) as unknown;
    const parsed = BotEventSubscriptionSchema.safeParse(body);
    if (!parsed.success) {
      this.logger.error('Invalid bot event subscription response %s', parsed.error.message);
      return { type: 'parseError', errors: parsed.error };
    }
    return { type: 'success', subscription: parsed.data };
  }
  async putBotEventSubscription(botId: string, subscription: Immutable<BotEventSubscription>): Promise<BotEventSubscriptionResult> {
    const response = await this.apiFetch(`/botframework/${botId}/meetings/subscription`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(subscription.eventTypes),
    });
    if (response.status < 200 || response.status >= 300) {
      return { type: 'networkError', errorCode: response.status };
    }

    return {
      type: 'success',
      subscription: {
        ...subscription,
        eventTypes: [...subscription.eventTypes]
      }
    };
  }
}
